import React, { useState, useEffect, useMemo } from 'react';
import { Field, Form } from 'react-final-form';

import DeleteIcon from '../../assets/icons/DeleteIcon';

import Button from '../shared/Button';
import Input from '../shared/Input';
import ModalSelectInput from '../shared/modal-select/ModalSelectInput';
import ModalSelect from '../shared/modal-select/ModalSelect';
import Checkbox from '../shared/Checkbox';

import { validateEmail } from '../../utils/validators';

import '../../styles/shared/form.scss';

const ProductTriggersForm = (props) => {
  const {
    mode = 'create',
    initialValues,
    buttonText,
    onSubmit,
    loading,
    products = [],
    procedures = [],
    onClose,
    withProcedures = false,
    withProducts = true,
    withToggle = true,
  } = props;

  const { name } = initialValues || {};

  const [isModalOpen, toggleModal] = useState(false);
  const [isProceduresModalOpen, toggleProceduresModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emails, setEmails] = useState([]);
  const [emailError, setEmailError] = useState(false);

  const formInitialValues = useMemo(() => {
    if (mode !== 'update') {
      return null;
    }

    const values = {
      ...initialValues,
    };

    if (withProducts) {
      values.products = initialValues?.products || [];
    }

    if (withProcedures) {
      values.procedures = initialValues?.procedures || [];
    }

    return values;
  }, [initialValues, mode, withProcedures, withProducts]);

  useEffect(() => {
    if (initialValues && initialValues.emails) {
      setEmails(initialValues.emails);
    }
  }, [initialValues]);

  const validate = (values) => {
    const errors = {};

    if (!values.name) {
      errors.name = 'Required';
    }

    return errors;
  };

  const getProductNames = (productIds) => {
    return productIds?.map((id) => {
      const product = products.find((item) => item.id === id);
      return product ? product.name : '';
    })
  };

  const getProcedureNames = (procedureIds) => {
    return procedureIds?.map((id) => {
      const procedure = procedures.find((item) => item.id === id);
      return procedure ? procedure.name : '';
    })
  };

  const onDeleteEmail = (email) => {
    const filterEmails = emails.filter((item) => item !== email);
    setEmails(filterEmails);
  }

  const onAddNewEmail = () => {
    const value = newEmail?.trim();
    if (value && !emails.includes(value) && validateEmail(value)) {
      setEmails([...emails, value]);
      setNewEmail('');
    } else {
      setEmailError(true);
    }
  };

  return (
    <div className='form__container'>
      <Form
        onSubmit={(formObj) => onSubmit(formObj, emails)}
        validate={validate}
        initialValues={formInitialValues}
        subscription={{ pristine: true, invalid: true, values: true }}
      >
        {
          ({ handleSubmit, submitting, invalid, pristine, values }) => (
            <form onSubmit={handleSubmit} className='form__body'>
              <div className='form__subtitle'>
                { name ? name : 'Add Trigger' }
              </div>
              { mode === 'update' && (
                <Field name='active'>
                  {({ input, meta }) => (
                    <Input
                      type='switch'
                      name={input.name}
                      value={input.value}
                      onChange={input.onChange}
                      placeholder='Status'
                    />
                  )}
                </Field>
              )}

              <div className='m-b-sm'>
                <div className='field__title'>Name</div>
                <Field name='name'>
                  {({ input, meta }) => (
                    <Input
                      name={input.name}
                      value={input.value}
                      placeholder='Name'
                      type='text'
                      onChange={input.onChange}
                    />
                  )}
                </Field>
              </div>

              <div className=''>
                <div className='field__title'>Emails</div>
                <div>
                  <div className='d-flex'>
                    <Input
                      type='email'
                      error={emailError}
                      placeholder='Email'
                      value={newEmail}
                      onChange={(e) => {
                        setNewEmail(e.target.value);
                        setEmailError(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.keyCode === 13) {
                          onAddNewEmail();
                        }
                      }}
                    />
                    <div className='m-b-lg m-l-md'>
                      <Button
                        type='submit'
                        text='Add'
                        onClick={onAddNewEmail}
                        width={100}
                        disabled={!newEmail.length}
                      />
                    </div>
                  </div>

                  <div className='m-b-lg form__select-users' style={{ minWidth: 'auto' }}>
                    {emails.length ? (
                      emails.map((email) => (
                        <div className='md-user-info' key={email}>
                          <div>
                            {email}
                          </div>
                          <div
                            className='md-user-info__delete'
                            onClick={() => onDeleteEmail(email)}
                          >
                            <DeleteIcon fontSize='small' color='#d32f2f' />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className='text-center secondary p-md'>
                        No Emails
                      </div>
                    )}
                  </div>
                </div>
              </div>

              { mode === 'update' && withProducts && (
                <Field name='allProducts' type='checkbox'>
                  {({ input }) => (
                    <div className='m-b-md'>
                      <Checkbox
                        input={input}
                        fontSize='small'
                        label='All brands'
                      />
                    </div>
                  )}
                </Field>
              )}

              { mode === 'update' && withProducts && !values?.allProducts && (
                <div className=''>
                  <div className='field__title'>Brands</div>
                  <Field name='products'>
                    {({ input, meta }) => (
                      <div className='m-b-lg'>
                        <ModalSelectInput
                          onClick={() => toggleModal(true)}
                          count={input.value.length}
                          label='Edit Brands'
                          values={getProductNames(input.value)}
                          icon='product'
                        />

                        <ModalSelect
                          title='Add Brands'
                          listTitle='Brands'
                          options={products}
                          onChange={input.onChange}
                          selected={input.value}
                          onClose={() => toggleModal(false)}
                          isOpen={isModalOpen}
                        />
                      </div>
                    )}
                  </Field>
                </div>
              )}

              { mode === 'update' && withProcedures && (
                <Field name='allProcedures' type='checkbox'>
                  {({ input }) => (
                    <div className='m-b-md'>
                      <Checkbox
                        input={input}
                        fontSize='small'
                        label='All procedures'
                      />
                    </div>
                  )}
                </Field>
              )}

              { mode === 'update' && withProcedures && !values?.allProcedures && (
                <div className=''>
                  <div className='field__title'>Procedures</div>
                  <Field name='procedures'>
                    {({ input, meta }) => (
                      <div className='m-b-lg'>
                        <ModalSelectInput
                          onClick={() => toggleProceduresModal(true)}
                          count={input.value.length}
                          label='Edit Procedures'
                          values={input.value ? getProcedureNames(input.value) : []}
                          icon='procedure'
                        />

                        <ModalSelect
                          title='Add Procedures'
                          listTitle='Procedures'
                          options={procedures}
                          onChange={input.onChange}
                          selected={input.value}
                          onClose={() => toggleProceduresModal(false)}
                          isOpen={isProceduresModalOpen}
                        />
                      </div>
                    )}
                  </Field>
                </div>
              )}

              {withToggle && (
                <>
                  <Field name='onApproveUsage'>
                    {({ input, meta }) => (
                      <Input
                        type='switch'
                        name={input.name}
                        value={input.value}
                        onChange={input.onChange}
                        placeholder='Trigger email on approved usage'
                      />
                    )}
                  </Field>

                  <Field name='onAllResolved'>
                    {({ input, meta }) => (
                      <Input
                        type='switch'
                        name={input.name}
                        value={input.value}
                        onChange={input.onChange}
                        placeholder='Resend on all resolved'
                      />
                    )}
                  </Field>
                </>
              )}

              <div className='form__actions'>
                <Button
                  type='submit'
                  text={buttonText || 'Save Changes'}
                  disabled={invalid || loading || !emails.length}
                  loading={loading}
                  onClick={handleSubmit}
                />
                { mode === 'create' && (
                  <Button
                    type='cancel'
                    onClick={onClose}
                  />
                )}
              </div>
            </form>
          )}
      </Form>
    </div>
  );
};

export default ProductTriggersForm;
